import { Link, useParams } from 'react-router-dom';
import { GlassButton } from '@/components/GlassButton';
import { Reveal } from '@/components/Reveal';
import { COURSE_BY_ID, DIRECTIONS, resolveLesson } from '@/lib/data';
import { useAppStore } from '@/lib/store';

export function CourseDetailPage(): JSX.Element {
  const { courseId = '' } = useParams<{ courseId: string }>();
  const customs = useAppStore((s) => s.customCourses);
  // Look up static course first; fall back to custom (which is a single-lesson course).
  const course = COURSE_BY_ID[courseId] ?? resolveLesson(courseId, customs)?.course;
  const completed = useAppStore((s) => new Set(s.progress.completedSentenceIds));

  if (course === undefined) {
    return (
      <div className="text-center py-20">
        <div className="text-6xl mb-4">🔍</div>
        <div className="text-xl text-white">课程不存在</div>
        <Link to="/app/courses" className="mt-4 inline-block text-honey-300 hover:text-honey-200">
          ← 回到课程市场
        </Link>
      </div>
    );
  }

  const dirMeta = DIRECTIONS.find((d) => d.key === course.direction) ?? DIRECTIONS[0]!;
  const totalSentences = course.lessons.reduce((acc, l) => acc + l.sentences.length, 0);
  const doneSentences = course.lessons.reduce(
    (acc, l) => acc + l.sentences.filter((s) => completed.has(s.id)).length,
    0,
  );
  const pct = totalSentences === 0 ? 0 : Math.round((doneSentences / totalSentences) * 100);

  return (
    <div className="space-y-10">
      <Reveal>
        <div>
          <Link to="/app/courses" className="text-sm text-cream-400 hover:text-white">
            ← 课程市场
          </Link>
          <div className="mt-3 flex items-start gap-4">
            <div className={`shrink-0 inline-flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br ${dirMeta.gradient} text-5xl`}>
              {course.emoji}
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.18em] text-honey-300 font-semibold">
                {dirMeta.title}
              </div>
              <h1 className="mt-1 font-display text-3xl md:text-5xl font-extrabold text-white">
                {course.title}
              </h1>
              <p className="mt-2 text-cream-300/90 max-w-2xl">{course.description}</p>
              <div className="mt-4 flex flex-wrap gap-2 text-xs text-cream-400">
                <Tag>📚 {course.lessons.length} 节课</Tag>
                <Tag>⏱ 约 {course.estimatedMinutes} 分钟</Tag>
                <Tag>难度 {'⭐'.repeat(course.difficulty)}</Tag>
                <Tag>{pct}% 已完成</Tag>
              </div>
            </div>
          </div>

          <div className="mt-6 h-2 rounded-full bg-white/[0.06] overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-honey-400 to-mint-400"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.05}>
        <div>
          <h2 className="font-display text-2xl font-bold text-white mb-5">课程目录</h2>
          <div className="space-y-3">
            {course.lessons.map((l, i) => {
              const done = l.sentences.filter((s) => completed.has(s.id)).length;
              const lessonPct = Math.round((done / l.sentences.length) * 100);
              const isDone = done === l.sentences.length;
              return (
                <div
                  key={l.id}
                  className="rounded-3xl border border-white/[0.06] bg-white/[0.02] p-5 hover:border-white/[0.14] transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`shrink-0 h-10 w-10 rounded-2xl grid place-items-center font-display font-bold ${
                      isDone
                        ? 'bg-mint-500/20 text-mint-300 border border-mint-500/40'
                        : 'bg-white/[0.04] text-cream-300 border border-white/[0.08]'
                    }`}>
                      {isDone ? '✓' : String(i + 1).padStart(2, '0')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-white">{l.title}</div>
                      <div className="text-xs text-cream-400 mt-0.5">
                        {l.sentences.length} 个句子 · {done}/{l.sentences.length} 完成
                      </div>
                      <div className="mt-2 h-1 rounded-full bg-white/[0.06] overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-honey-400 to-mint-400"
                          style={{ width: `${lessonPct}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <GlassButton to={`/app/study/puzzle/${l.id}`} size="sm" variant="solid">
                        🧩 拼图
                      </GlassButton>
                      <div className="flex gap-1.5">
                        <GlassButton to={`/app/study/typing/${l.id}`} size="sm">⌨️</GlassButton>
                        <GlassButton to={`/app/study/speaking/${l.id}`} size="sm">🎙️</GlassButton>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Reveal>
    </div>
  );
}

function Tag({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-white/[0.08] bg-white/[0.03] px-2.5 py-1">
      {children}
    </span>
  );
}
